import { useEffect, useRef } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, useMap } from "react-leaflet";

// Disable default Leaflet icon URL hacks since we're using custom DivIcons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png";
const ATTR =
  '&copy; <a href="https://openstreetmap.org">OSM</a> · <a href="https://carto.com/attributions">CARTO</a>';

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.flyTo([center.lat, center.lng], map.getZoom(), { animate: true, duration: 0.8 });
  }, [center, map]);
  return null;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function MarkersLayer({ meetings, onMarkerClick, userPosition }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(map);
    }
    const layer = layerRef.current;
    layer.clearLayers();

    meetings.forEach((m) => {
      const occupied = m.status !== "open";
      const hasAccommodation = Boolean(m.accommodationId || m.placeUuid);
      const html = `<div class="zx-marker ${occupied ? "occupied" : ""}${hasAccommodation ? " with-accommodation" : ""}"></div>`;
      const icon = L.divIcon({
        className: "zx-divicon",
        html,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
      const marker = L.marker([m.approxLat, m.approxLng], { icon });
      const hotelLine = m.placeName
        ? `<div style="font-family:'Space Mono',monospace;font-size:11px;color:#e9b5c5;margin-top:6px;">Hospedaje · ${escapeHtml(m.placeName)}</div>`
        : hasAccommodation
          ? `<div style="font-family:'Space Mono',monospace;font-size:10px;color:rgba(255,255,255,0.45);margin-top:6px;">Con hospedaje asociado</div>`
          : "";
      const popupHtml = `
        <div style="min-width:200px">
          <div style="font-family:'Cormorant Garamond',serif;font-size:18px;color:#f5f5f5;letter-spacing:0.02em;">${m.type === "2+2" ? "Encuentro · 2+2" : "Encuentro · 1+1"}</div>
          <div style="font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.18em;text-transform:uppercase;color:rgba(255,255,255,0.55);margin-top:4px;">${occupied ? "Cupo cerrado" : "Cupo abierto"}</div>
          <div style="font-family:'Space Mono',monospace;font-size:11px;color:rgba(255,255,255,0.75);margin-top:8px;">Org. <strong style="color:#e9b5c5">${escapeHtml(m.organizerNick || "Anónimo")}</strong></div>
          ${hotelLine}
          <div style="font-family:'Space Mono',monospace;font-size:11px;color:rgba(255,255,255,0.55);margin-top:2px;">${new Date(m.time).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}</div>
          <button id="zx-popup-${m.id}" style="margin-top:12px;width:100%;background:#8B1E3F;color:#f5f5f5;border:none;padding:10px 12px;font-family:'Space Mono',monospace;text-transform:uppercase;letter-spacing:0.18em;font-size:10px;cursor:pointer;">Ver detalle →</button>
        </div>
      `;
      marker.bindPopup(popupHtml, { closeButton: true, autoPan: true });
      marker.on("popupopen", () => {
        const btn = document.getElementById(`zx-popup-${m.id}`);
        if (btn) btn.onclick = () => onMarkerClick && onMarkerClick(m);
      });
      marker.addTo(layer);
    });

    if (userPosition) {
      const selfHtml = `<div class="zx-marker self"></div>`;
      const icon = L.divIcon({
        className: "zx-divicon",
        html: selfHtml,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
      L.marker([userPosition.lat, userPosition.lng], { icon, interactive: false }).addTo(layer);
    }
  }, [meetings, userPosition, map, onMarkerClick]);

  return null;
}

function LinkedAccommodationMarkers({ meetings, places = [] }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!layerRef.current) {
      layerRef.current = L.layerGroup().addTo(map);
    }
    const layer = layerRef.current;
    layer.clearLayers();

    const linkedIds = new Set(
      meetings.map((m) => m.accommodationId ?? m.placeUuid).filter(Boolean)
    );
    if (!linkedIds.size || !places.length) return;

    places
      .filter((p) => linkedIds.has(p.uuid) && p.lat != null && p.lng != null)
      .forEach((place) => {
        const html = `<div class="zx-marker place linked-accommodation"></div>`;
        const icon = L.divIcon({
          className: "zx-divicon",
          html,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });
        L.marker([place.lat, place.lng], { icon, interactive: false }).addTo(layer);
      });
  }, [meetings, places, map]);

  return null;
}

function RadiusCircle({ center, radiusKm }) {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (!center) return;
    if (ref.current) {
      map.removeLayer(ref.current);
      ref.current = null;
    }
    ref.current = L.circle([center.lat, center.lng], {
      radius: radiusKm * 1000,
      color: "#8B1E3F",
      weight: 1,
      opacity: 0.45,
      fillColor: "#8B1E3F",
      fillOpacity: 0.05,
      interactive: false,
    }).addTo(map);
    return () => {
      if (ref.current) map.removeLayer(ref.current);
    };
  }, [center, radiusKm, map]);
  return null;
}

export default function MeetingMap({
  center,
  meetings = [],
  places = [],
  userPosition = null,
  radiusKm = 25,
  onMarkerClick,
}) {
  return (
    <div
      className="absolute inset-0 z-0"
      data-testid="meeting-map"
    >
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={12}
        style={{ height: "100%", width: "100%", background: "#050505" }}
        zoomControl={true}
        attributionControl={true}
      >
        <TileLayer attribution={ATTR} url={DARK_TILES} />
        <FlyTo center={center} />
        <MarkersLayer
          meetings={meetings}
          userPosition={userPosition}
          onMarkerClick={onMarkerClick}
        />
        <LinkedAccommodationMarkers meetings={meetings} places={places} />
        <RadiusCircle center={userPosition || center} radiusKm={radiusKm} />
      </MapContainer>
    </div>
  );
}
